import React, { useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import * as Fuse from 'fuse.js';
import Checkbox from 'material-ui/Checkbox';
import TextField from 'material-ui/TextField';
import {
    Table,
    TableBody,
    TableHeader,
    TableHeaderColumn,
    TableRow,
    TableRowColumn,
} from 'material-ui/Table';
import { formatCompare, formatMetric } from './metrics';

const VISIBLE_ROWS = 12;
const ROW_HEIGHT = 48;
const METRICS = ['lhsCandidates', 'rhsFirings', 'annotationsCreated'];

const COLUMNS = [
    { key: 'phase', label: 'Phase' },
    { key: 'rule', label: 'Rule' },
    { key: 'lhsCandidates', label: 'LHS' },
    { key: 'rhsFirings', label: 'RHS' },
    { key: 'annotationsCreated', label: 'Annotations' },
];

const FUSE_OPTIONS = {
    keys: ['phase', 'rule'],
    shouldSort: false,
    threshold: 0.15,
    distance: 240,
    maxPatternLength: 64,
    minMatchCharLength: 1,
};

function compareValues(left, right) {
    const leftMissing = left === undefined || left === null;
    const rightMissing = right === undefined || right === null;
    if (leftMissing && rightMissing) {
        return 0;
    }
    if (leftMissing) {
        return 1;
    }
    if (rightMissing) {
        return -1;
    }
    if (typeof left === 'number' && typeof right === 'number') {
        return left - right;
    }
    return String(left).localeCompare(String(right), undefined, {
        numeric: true,
        sensitivity: 'base',
    });
}

function ruleKey(row) {
    return `${row.docName}\0${row.phase}\0${row.rule}`;
}

function metricSnapshot(row) {
    return {
        lhsCandidates: row.lhsCandidates,
        rhsFirings: row.rhsFirings,
        annotationsCreated: row.annotationsCreated,
    };
}

function mergeRuleRows(baseRows, targetRows) {
    const baseOrder = [];
    const baseByKey = new Map();
    (baseRows || []).forEach((row) => {
        const key = ruleKey(row);
        if (!baseByKey.has(key)) {
            baseOrder.push(key);
            baseByKey.set(key, row);
        }
    });
    const targetOrder = [];
    const targetByKey = new Map();
    (targetRows || []).forEach((row) => {
        const key = ruleKey(row);
        if (!targetByKey.has(key)) {
            targetOrder.push(key);
            targetByKey.set(key, row);
        }
    });
    const both = [];
    const removed = [];
    baseOrder.forEach((key) => {
        const base = baseByKey.get(key);
        const target = targetByKey.get(key);
        if (target) {
            both.push({
                docName: base.docName,
                phase: base.phase,
                rule: base.rule,
                presence: 'both',
                base: metricSnapshot(base),
                target: metricSnapshot(target),
            });
            return;
        }
        removed.push({
            docName: base.docName,
            phase: base.phase,
            rule: base.rule,
            presence: 'removed',
            base: metricSnapshot(base),
            target: null,
        });
    });
    const added = [];
    targetOrder.forEach((key) => {
        if (baseByKey.has(key)) {
            return;
        }
        const target = targetByKey.get(key);
        added.push({
            docName: target.docName,
            phase: target.phase,
            rule: target.rule,
            presence: 'added',
            base: null,
            target: metricSnapshot(target),
        });
    });
    return [...both, ...removed, ...added];
}

function addMetrics(acc, row) {
    const next = acc || { lhsCandidates: 0, rhsFirings: 0, annotationsCreated: 0 };
    METRICS.forEach((key) => {
        if (typeof row[key] === 'number') {
            next[key] += row[key];
        }
    });
    return next;
}

function groupByPhaseName(rows) {
    const order = [];
    const groups = new Map();
    rows.forEach((row) => {
        let group = groups.get(row.phase);
        if (!group) {
            group = {
                docName: '',
                phase: row.phase,
                rule: '',
                grouped: true,
                base: null,
                target: null,
                members: [],
            };
            groups.set(row.phase, group);
            order.push(row.phase);
        }
        group.members.push(row);
        if (row.base) {
            group.base = addMetrics(group.base, row.base);
        }
        if (row.target) {
            group.target = addMetrics(group.target, row.target);
        }
        if (METRICS.some(key => typeof row[key] === 'number')) {
            const totals = addMetrics(group.totals, row);
            group.totals = totals;
            METRICS.forEach((key) => {
                group[key] = totals[key];
            });
        }
    });
    const grouped = order.map((phase) => {
        const group = groups.get(phase);
        if (group.base && group.target) {
            group.presence = 'both';
        } else if (group.base) {
            group.presence = 'removed';
        } else if (group.target) {
            group.presence = 'added';
        }
        return group;
    });
    if (!grouped.some(row => row.presence)) {
        return grouped;
    }
    return [
        ...grouped.filter(row => row.presence === 'both'),
        ...grouped.filter(row => row.presence === 'removed'),
        ...grouped.filter(row => row.presence === 'added'),
    ];
}

function metricsDiffer(row) {
    return METRICS.some((key) => {
        const left = row.base ? row.base[key] : undefined;
        const right = row.target ? row.target[key] : undefined;
        return left !== right;
    });
}

function sortValue(row, key, compareMode) {
    if (!compareMode || key === 'phase' || key === 'rule') {
        return row[key];
    }
    const target = row.target && row.target[key];
    if (target !== undefined && target !== null) {
        return target;
    }
    const base = row.base && row.base[key];
    if (base !== undefined && base !== null) {
        return base;
    }
    return null;
}

function MetricPair({ base, target, metric }) {
    const left = formatMetric(base ? base[metric] : undefined);
    const right = formatMetric(target ? target[metric] : undefined);
    return <span>{formatCompare(left, right)}</span>;
}

function rowIdentity(row) {
    if (row.grouped) {
        return `phase:${row.phase}`;
    }
    return `${row.docName}-${row.phase}-${row.rule}`;
}

function hoverWrap(node, hover) {
    if (!hover) {
        return node;
    }
    return (
        <span onMouseEnter={hover.onEnter} onMouseLeave={hover.onLeave}>
            {node}
        </span>
    );
}

function ruleColumns(row, compareMode, showRule, hover) {
    const columns = [
        <TableRowColumn key="phase">{hoverWrap(row.phase, hover)}</TableRowColumn>,
    ];
    if (showRule) {
        columns.push(<TableRowColumn key="rule">{hoverWrap(row.rule, hover)}</TableRowColumn>);
    }
    METRICS.forEach((metric) => {
        const value = compareMode
            ? <MetricPair base={row.base} target={row.target} metric={metric} />
            : formatMetric(row[metric]);
        columns.push(<TableRowColumn key={metric}>{hoverWrap(value, hover)}</TableRowColumn>);
    });
    return columns;
}

function PhaseRulesPopover({
    tip,
    compareMode,
    onEnter,
    onLeave,
}) {
    return ReactDOM.createPortal(
        <div
            className="test-runs-phase-popover"
            style={{
                top: tip.top,
                left: tip.left,
                width: tip.width,
                maxWidth: tip.width,
                transform: tip.above ? 'translateY(-100%)' : undefined,
            }}
            onMouseEnter={onEnter}
            onMouseLeave={onLeave}
        >
            <Table selectable={false}>
                <TableHeader displaySelectAll={false} adjustForCheckbox={false}>
                    <TableRow>
                        {COLUMNS.map(col => (
                            <TableHeaderColumn key={col.key}>{col.label}</TableHeaderColumn>
                        ))}
                    </TableRow>
                </TableHeader>
                <TableBody displayRowCheckbox={false}>
                    {tip.members.map(row => (
                        <TableRow key={rowIdentity(row)} selectable={false}>
                            {ruleColumns(row, compareMode, true)}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </div>,
        document.body,
    );
}

export default function JapeRulesTable({
    rows,
    baseRows,
    targetRows,
    differencesOnly,
    onDifferencesOnly,
    groupByPhase,
    onGroupByPhase,
}) {
    const compareMode = Array.isArray(baseRows) && Array.isArray(targetRows);
    const [query, setQuery] = useState('');
    const [sortKey, setSortKey] = useState(null);
    const [sortDir, setSortDir] = useState('asc');
    const [phaseTip, setPhaseTip] = useState({
        open: false,
        top: 0,
        left: 0,
        width: 0,
        above: false,
        members: [],
    });
    const closeTimer = useRef(0);

    const clearClose = () => {
        window.clearTimeout(closeTimer.current);
    };

    const scheduleClose = () => {
        clearClose();
        closeTimer.current = window.setTimeout(() => {
            setPhaseTip(current => ({ ...current, open: false }));
        }, 150);
    };

    const openPhase = (row, node) => {
        clearClose();
        const rowRect = node.getBoundingClientRect();
        const table = node.closest('table') || node;
        const tableRect = table.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rowRect.bottom;
        const openAbove = spaceBelow < 180 && rowRect.top > spaceBelow;
        setPhaseTip({
            open: true,
            top: openAbove ? rowRect.top - 4 : rowRect.bottom + 4,
            left: tableRect.left,
            width: tableRect.width,
            above: openAbove,
            members: row.members || [],
        });
    };

    const sourceRows = useMemo(() => {
        const list = compareMode ? mergeRuleRows(baseRows, targetRows) : (rows || []);
        if (!groupByPhase) {
            return list;
        }
        return groupByPhaseName(list);
    }, [baseRows, compareMode, groupByPhase, rows, targetRows]);

    const columns = useMemo(() => {
        if (groupByPhase) {
            return COLUMNS.filter(col => col.key !== 'rule');
        }
        return COLUMNS;
    }, [groupByPhase]);

    const fuse = useMemo(() => new Fuse(sourceRows, FUSE_OPTIONS), [sourceRows]);

    const filtered = useMemo(() => {
        const text = query.trim().slice(0, FUSE_OPTIONS.maxPatternLength);
        let next = sourceRows;
        if (text) {
            next = fuse.search(text);
        }
        if (compareMode && differencesOnly) {
            next = next.filter(metricsDiffer);
        }
        return next;
    }, [compareMode, differencesOnly, fuse, query, sourceRows]);

    const sorted = useMemo(() => {
        if (!sortKey) {
            return filtered;
        }
        const copy = filtered.slice();
        copy.sort((left, right) => {
            const cmp = compareValues(
                sortValue(left, sortKey, compareMode),
                sortValue(right, sortKey, compareMode),
            );
            return sortDir === 'asc' ? cmp : -cmp;
        });
        return copy;
    }, [compareMode, filtered, sortDir, sortKey]);

    const onSort = (key) => {
        if (sortKey === key) {
            setSortDir(dir => (dir === 'asc' ? 'desc' : 'asc'));
            return;
        }
        setSortKey(key);
        setSortDir('desc');
    };

    const visibleCount = Math.min(Math.max(sorted.length, 1), VISIBLE_ROWS);
    const bodyHeight = `${visibleCount * ROW_HEIGHT}px`;

    return (
        <div className="test-runs-jape">
            <div className="test-runs-jape-toolbar">
                <TextField
                    className="test-runs-jape-filter"
                    hintText={groupByPhase ? 'Filter by phase' : 'Filter by phase or rule'}
                    value={query}
                    onChange={(event, value) => setQuery(value)}
                    fullWidth
                />
                {compareMode ? (
                    <Checkbox
                        label="Differences only"
                        checked={differencesOnly}
                        onCheck={(event, checked) => onDifferencesOnly(checked)}
                        style={{ width: 'auto' }}
                    />
                ) : null}
                <Checkbox
                    label="Group by phase"
                    checked={groupByPhase}
                    onCheck={(event, checked) => onGroupByPhase(checked)}
                    style={{ width: 'auto' }}
                />
                {compareMode ? (
                    <span className="test-runs-jape-count">base · this run</span>
                ) : null}
                <span className="test-runs-jape-count">
                    {sorted.length} / {sourceRows.length}
                </span>
            </div>
            <Table
                selectable={false}
                fixedHeader
                fixedFooter={false}
                height={bodyHeight}
            >
                <TableHeader displaySelectAll={false} adjustForCheckbox={false}>
                    <TableRow>
                        {columns.map(col => (
                            <TableHeaderColumn
                                key={col.key}
                                style={{ padding: 0 }}
                            >
                                <div
                                    className="test-runs-jape-header"
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => onSort(col.key)}
                                    onKeyDown={(event) => {
                                        if (event.key === 'Enter' || event.key === ' ') {
                                            event.preventDefault();
                                            onSort(col.key);
                                        }
                                    }}
                                >
                                    {col.label}
                                    {sortKey === col.key && sortDir === 'asc' ? ' ▲' : ''}
                                    {sortKey === col.key && sortDir === 'desc' ? ' ▼' : ''}
                                </div>
                            </TableHeaderColumn>
                        ))}
                    </TableRow>
                </TableHeader>
                <TableBody displayRowCheckbox={false}>
                    {sorted.map((row) => {
                        const hover = row.grouped ? {
                            onEnter: (event) => {
                                const rowNode = event.currentTarget.closest('tr') || event.currentTarget;
                                openPhase(row, rowNode);
                            },
                            onLeave: scheduleClose,
                        } : null;
                        return (
                            <TableRow key={rowIdentity(row)} selectable={false}>
                                {ruleColumns(row, compareMode, !groupByPhase, hover)}
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
            {phaseTip.open ? (
                <PhaseRulesPopover
                    tip={phaseTip}
                    compareMode={compareMode}
                    onEnter={clearClose}
                    onLeave={scheduleClose}
                />
            ) : null}
        </div>
    );
}



// WEBPACK FOOTER //
// ./app/components/TestRuns/JapeRulesTable.jsx